import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import WorkflowIndicator from '../components/WorkflowIndicator';
import { CreateLeadModal, EditLeadModal } from '../components/LeadModals';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_PRIMARY = '#2b3380';
const ZOBOX_HOVER = '#1f2660';
const ZOBOX_LIGHT = '#2b338015';

function FranchiseeManagementUnified({ user, onLogout }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeMainTab, setActiveMainTab] = useState(searchParams.get('main') || 'leads');
  const [activeSubTab, setActiveSubTab] = useState(searchParams.get('sub') || '');
  const [loading, setLoading] = useState(false);

  // Data states
  const [leads, setLeads] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [journeys, setJourneys] = useState([]);
  const [selectedJourney, setSelectedJourney] = useState(null);
  const [journeyDetails, setJourneyDetails] = useState(null);
  const [franchisees, setFranchisees] = useState([]);
  const [dailyData, setDailyData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [plans, setPlans] = useState([]);
  const [storeSetups, setStoreSetups] = useState([]);
  const [selectedSetup, setSelectedSetup] = useState(null);

  // Modal states
  const [selectedLead, setSelectedLead] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCreateLeadModal, setShowCreateLeadModal] = useState(false);
  const [showEditLeadModal, setShowEditLeadModal] = useState(false);
  const [leadToEdit, setLeadToEdit] = useState(null);

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const mainTabs = [
    { id: 'leads', name: 'Leads', icon: '🎯', description: 'Lead generation to token payment' },
    { id: 'journey', name: 'Journey Pipeline', icon: '🛤️', description: 'Token to pre-launch tracking' },
    { id: 'setup', name: 'Store Setup', icon: '🚀', description: 'Design to launch preparation' },
    { id: 'active', name: 'Active Franchisees', icon: '🏪', description: 'Live store operations' }
  ];

  const subTabs = {
    leads: [
      { id: 'dashboard', name: 'Dashboard', icon: '📊' },
      { id: 'all-leads', name: 'All Leads', icon: '📋' },
      { id: 'my-tasks', name: 'My Tasks', icon: '✓' }
    ],
    journey: [
      { id: 'timeline', name: 'Timeline View', icon: '📊' },
      { id: 'gantt', name: 'Gantt Chart', icon: '📈' },
      { id: 'payments', name: 'Payments', icon: '💰' },
      { id: 'documents', name: 'Documents', icon: '📄' },
      { id: 'approvals', name: 'Approvals', icon: '✅' }
    ],
    setup: [
      { id: 'overview', name: 'Overview', icon: '📊' },
      { id: 'design', name: 'Design Approval', icon: '🎨' },
      { id: 'stock', name: 'Stock Allocation', icon: '📦' },
      { id: 'training', name: 'Training', icon: '👨‍🎓' },
      { id: 'checklist', name: 'Launch Checklist', icon: '✅' }
    ],
    active: [
      { id: 'franchisees', name: 'All Franchisees', icon: '🏪' },
      { id: 'plans', name: 'Franchisee Plans', icon: '📋' },
      { id: 'performance', name: 'Performance', icon: '📊' },
      { id: 'daily-ops', name: 'Daily Operations', icon: '📅' },
      { id: 'monthly', name: 'Monthly Performance', icon: '📈' },
      { id: 'commissions', name: 'Commissions', icon: '💰' },
      { id: 'inventory', name: 'Inventory & Stock', icon: '📦' },
      { id: 'support', name: 'Support & Issues', icon: '🎫' }
    ]
  };

  useEffect(() => {
    const main = searchParams.get('main');
    const sub = searchParams.get('sub');
    if (main) setActiveMainTab(main);
    if (sub) setActiveSubTab(sub);
    else {
      // Set default sub-tab based on main tab
      if (main === 'leads') setActiveSubTab('dashboard');
      else if (main === 'journey') setActiveSubTab('timeline');
      else if (main === 'setup') setActiveSubTab('overview');
      else if (main === 'active') setActiveSubTab('franchisees');
    }
  }, [searchParams]);

  useEffect(() => {
    loadDataForTab();
  }, [activeMainTab, activeSubTab]);

  const loadDataForTab = async () => {
    if (activeMainTab === 'leads') {
      await fetchLeads(); // Always load leads for dashboard
      if (activeSubTab === 'my-tasks') await fetchMyTasks();
    } else if (activeMainTab === 'journey') {
      await fetchJourneys();
    } else if (activeMainTab === 'setup') {
      await fetchStoreSetups();
    } else if (activeMainTab === 'active') {
      if (activeSubTab === 'franchisees') await fetchFranchisees();
      else if (activeSubTab === 'plans') {
        await fetchPlans();
        await fetchFranchisees();
      }
      else if (activeSubTab === 'daily-ops') await fetchDailyOperations();
      else if (activeSubTab === 'monthly') await fetchMonthlyData();
    }
  };

  // ==================== DATA FETCHING ====================
  const fetchLeads = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(response.data.leads || []);
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyTasks = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // OPTIMIZED: Use bulk API to get all leads with followups in single query
      const response = await axios.get(`${API}/api/bulk/leads-with-followups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const allFollowups = [];
      for (const lead of response.data.leads || []) {
        if (lead.followups && lead.followups.length > 0) {
          lead.followups.forEach(followup => {
            allFollowups.push({
              ...followup,
              lead_name: lead.applicant_name,
              lead_phone: lead.phone,
              lead_city: lead.city,
              lead_status: lead.status
            });
          });
        }
      }
      setFollowups(allFollowups);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchJourneys = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/franchisee-journey`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJourneys(response.data.journeys || []);
      if (response.data.journeys && response.data.journeys.length > 0) {
        setSelectedJourney(response.data.journeys[0]);
        await fetchJourneyDetails(response.data.journeys[0].journey_id);
      }
    } catch (error) {
      console.error('Error fetching journeys:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchJourneyDetails = async (journeyId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/franchisee-journey/${journeyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJourneyDetails(response.data);
    } catch (error) {
      console.error('Error fetching journey details:', error);
    }
  };

  const fetchFranchisees = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/stores`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFranchisees(response.data.stores || []);
    } catch (error) {
      console.error('Error fetching franchisees:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDailyOperations = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // OPTIMIZED: Use bulk API to get all franchisees with today's sales in single query
      const response = await axios.get(`${API}/api/bulk/franchisees-with-sales`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const dailyOps = response.data.franchisees.map(franchisee => ({
        store: franchisee,
        sales: franchisee.sales || null
      }));
      
      setDailyData(dailyOps);
    } catch (error) {
      console.error('Error fetching daily operations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlyData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // OPTIMIZED: Use bulk API with MongoDB aggregation for monthly performance
      const now = new Date();
      const response = await axios.get(
        `${API}/api/bulk/franchisees-monthly-performance?month=${now.getMonth() + 1}&year=${now.getFullYear()}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      const monthlyPerf = response.data.franchisees.map(franchisee => ({
        store: franchisee,
        total_sales: franchisee.performance?.total_sales || 0,
        days_active: franchisee.performance?.days_active || 0,
        avg_daily_sales: franchisee.performance?.avg_daily_sales || 0
      }));
      
      setMonthlyData(monthlyPerf);
    } catch (error) {
      console.error('Error fetching monthly data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/franchisee-plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlans(response.data.plans || []);
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStoreSetups = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/store-setup`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStoreSetups(response.data.setups || []);
      if (response.data.setups && response.data.setups.length > 0) {
        setSelectedSetup(response.data.setups[0]);
      }
    } catch (error) {
      console.error('Error fetching store setups:', error);
      setStoreSetups([]);
    } finally {
      setLoading(false);
    }
  };


  const handleMainTabChange = (tabId) => {
    setActiveMainTab(tabId);
    const defaultSubTab = subTabs[tabId]?.[0]?.id || '';
    setActiveSubTab(defaultSubTab);
    setSearchParams({ main: tabId, sub: defaultSubTab });
  };

  const handleSubTabChange = (tabId) => {
    setActiveSubTab(tabId);
    setSearchParams({ main: activeMainTab, sub: tabId });
  };

  const openLeadDetail = async (lead) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/leads/${lead.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedLead(response.data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error fetching lead details:', error);
    }
  };

  // Workflow steps for Franchisee Management
  const franchiseeWorkflowSteps = [
    { id: 'lead', label: 'Lead Generation' },
    { id: 'followup', label: 'Follow-up' },
    { id: 'token', label: 'Token Payment' },
    { id: 'journey', label: 'Journey Pipeline' },
    { id: 'prelaunch', label: 'Pre-launch' },
    { id: 'setup', label: 'Store Setup' },
    { id: 'active', label: 'Active Store' }
  ];

  // Determine current step based on active tab
  const getCurrentWorkflowStep = () => {
    if (activeMainTab === 'leads') return 'lead';
    if (activeMainTab === 'journey') return 'journey';
    if (activeMainTab === 'setup') return 'setup';
    if (activeMainTab === 'active') return 'active';
    return 'lead';
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Franchisee Management</h1>
          <p className="mt-2 text-gray-600">Complete lifecycle management from Lead to Live Store</p>
        </div>

        {/* Workflow Indicator */}
        <WorkflowIndicator 
          steps={franchiseeWorkflowSteps} 
          currentStep={getCurrentWorkflowStep()} 
        />

        {/* Main Tabs */}
        <div className="mb-6">
          <div className="grid grid-cols-3 gap-6">
            {mainTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleMainTabChange(tab.id)}
                className={`p-6 rounded-lg border-2 transition-all ${
                  activeMainTab === tab.id
                    ? 'bg-white'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
                style={activeMainTab === tab.id ? {
                  borderColor: ZOBOX_PRIMARY,
                  backgroundColor: ZOBOX_LIGHT
                } : {}}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-3xl">{tab.icon}</span>
                  {activeMainTab === tab.id && (
                    <span className="text-xl" style={{ color: ZOBOX_PRIMARY }}>●</span>
                  )}
                </div>
                <div className="text-left">
                  <div className="text-lg font-semibold" style={activeMainTab === tab.id ? { color: ZOBOX_PRIMARY } : { color: '#111827' }}>
                    {tab.name}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">{tab.description}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Sub Tabs */}
        {subTabs[activeMainTab] && (
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8 overflow-x-auto">
              {subTabs[activeMainTab].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleSubTabChange(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeSubTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        )}

        {/* Content Area */}
        <div>
          {loading && <div className="text-center py-12">Loading...</div>}
          
          {!loading && activeMainTab === 'leads' && (
            <>
              {activeSubTab === 'all-leads' && (
                <AllLeadsTab 
                  leads={leads}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  statusFilter={statusFilter}
                  setStatusFilter={setStatusFilter}
                  openLeadDetail={openLeadDetail}
                  onEditLead={(lead) => {
                    setLeadToEdit(lead);
                    setShowEditLeadModal(true);
                  }}
                />
              )}
              {activeSubTab === 'my-tasks' && (
                <MyTasksTab followups={followups} openLeadDetail={openLeadDetail} />
              )}
              {activeSubTab === 'dashboard' && (
                <DashboardTab leads={leads} setShowCreateLeadModal={setShowCreateLeadModal} />
              )}
            </>
          )}

          {!loading && activeMainTab === 'journey' && (
            <>
              {journeys.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-12 text-center">
                  <div className="text-6xl mb-4">🚀</div>
                  <h3 className="text-xl font-semibold mb-2">No Franchisee Journeys Found</h3>
                  <p className="text-gray-600">Convert approved leads to start tracking their journey</p>
                </div>
              ) : (
                <>
                  {/* Journey Selector */}
                  <div className="mb-6">
                    <div className="w-full max-w-md">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Select Franchisee Journey</label>
                      <select
                        value={selectedJourney?.journey_id || ''}
                        onChange={(e) => {
                          const journey = journeys.find(j => j.journey_id === e.target.value);
                          setSelectedJourney(journey);
                          fetchJourneyDetails(e.target.value);
                        }}
                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#2b3380]"
                      >
                        {journeys.map(journey => (
                          <option key={journey.journey_id} value={journey.journey_id}>
                            {journey.franchisee_name} - {journey.current_stage}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Journey Progress Card */}
                  {selectedJourney && (
                    <div className="rounded-lg shadow-lg p-6 text-white mb-6" style={{ background: 'linear-gradient(to right, #2b3380, #1f2660)' }}>
                      <div className="flex justify-between items-start">
                        <div>
                          <h2 className="text-2xl font-bold">{selectedJourney.franchisee_name}</h2>
                          <p className="mt-1 opacity-90">{selectedJourney.franchisee_phone} • {selectedJourney.franchisee_email}</p>
                          <div className="mt-3">
                            <span className="px-3 py-1 bg-white bg-opacity-20 rounded-full text-sm">
                              {selectedJourney.selected_plan || 'Plan Not Selected'}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm opacity-90">Current Stage</div>
                          <div className="text-xl font-bold">{selectedJourney.current_stage}</div>
                          {selectedJourney.total_investment > 0 && (
                            <div className="mt-2 text-sm">
                              Investment: ₹{(selectedJourney.total_investment / 100000).toFixed(1)}L
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Status Cards */}
                  {selectedJourney && (
                    <div className="grid grid-cols-5 gap-4 mb-6">
                      <StatusCard title="Token" status={selectedJourney.token_paid} icon="💰" amount={selectedJourney.token_amount} />
                      <StatusCard title="Agreement" status={selectedJourney.agreement_signed} icon="📄" />
                      <StatusCard title="Payment" status={selectedJourney.payment_complete} icon="💳" />
                      <StatusCard title="GST" status={selectedJourney.gst_status === "Approved"} icon="📝" subtitle={selectedJourney.gst_status} />
                      <StatusCard title="Store" status={selectedJourney.store_created} icon="🏪" subtitle={selectedJourney.store_code} />
                    </div>
                  )}

                  {/* Journey Content */}
                  {activeSubTab === 'timeline' && journeyDetails && (
                    <TimelineView journey={selectedJourney} details={journeyDetails} />
                  )}
                  {activeSubTab === 'gantt' && journeyDetails && (
                    <GanttChartView journey={selectedJourney} details={journeyDetails} />
                  )}
                  {activeSubTab === 'payments' && journeyDetails && (
                    <PaymentsView journey={selectedJourney} details={journeyDetails} />
                  )}
                  {activeSubTab === 'documents' && journeyDetails && (
                    <DocumentsView journey={selectedJourney} details={journeyDetails} />
                  )}
                  {activeSubTab === 'approvals' && journeyDetails && (
                    <ApprovalsView journey={selectedJourney} details={journeyDetails} />
                  )}
                </>
              )}
            </>
          )}

          {!loading && activeMainTab === 'setup' && (
            <div className="space-y-6">
              {/* Setup Selector */}
              {storeSetups.length > 0 && (
                <div className="bg-white rounded-lg shadow p-4 flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-1">Select Store Setup</h3>
                    <p className="text-xs text-gray-500">Choose a store to manage its setup process</p>
                  </div>
                  <select
                    value={selectedSetup?.setup_id || ''}
                    onChange={(e) => {
                      const setup = storeSetups.find(s => s.setup_id === e.target.value);
                      setSelectedSetup(setup);
                    }}
                    className="w-64 border border-gray-300 rounded-lg px-3 py-2"
                  >
                    {storeSetups.map(setup => (
                      <option key={setup.setup_id} value={setup.setup_id}>
                        {setup.store_name} ({setup.store_code})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {storeSetups.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-12 text-center">
                  <div className="text-6xl mb-4">🚀</div>
                  <h3 className="text-xl font-semibold mb-2">No Store Setups</h3>
                  <p className="text-gray-600">Stores in journey pipeline will appear here when they reach the setup phase</p>
                </div>
              ) : (
                <>
                  {activeSubTab === 'overview' && (
                    <div className="bg-white rounded-lg shadow p-6">
                      <h3 className="text-lg font-semibold mb-4">Setup Overview</h3>
                      <div className="grid grid-cols-4 gap-4">
                        <div className="border rounded-lg p-4">
                          <div className="text-2xl mb-2">🎨</div>
                          <div className="text-sm text-gray-600">Design</div>
                          <div className="text-lg font-semibold" style={{ color: ZOBOX_PRIMARY }}>
                            {selectedSetup?.design_status || 'Pending'}
                          </div>
                        </div>
                        <div className="border rounded-lg p-4">
                          <div className="text-2xl mb-2">📦</div>
                          <div className="text-sm text-gray-600">Stock</div>
                          <div className="text-lg font-semibold" style={{ color: ZOBOX_PRIMARY }}>
                            {selectedSetup?.stock_status || 'Pending'}
                          </div>
                        </div>
                        <div className="border rounded-lg p-4">
                          <div className="text-2xl mb-2">👨‍🎓</div>
                          <div className="text-sm text-gray-600">Training</div>
                          <div className="text-lg font-semibold" style={{ color: ZOBOX_PRIMARY }}>
                            {selectedSetup?.training_status || 'Pending'}
                          </div>
                        </div>
                        <div className="border rounded-lg p-4">
                          <div className="text-2xl mb-2">✅</div>
                          <div className="text-sm text-gray-600">Launch</div>
                          <div className="text-lg font-semibold" style={{ color: ZOBOX_PRIMARY }}>
                            {selectedSetup?.launch_status || 'Pending'}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeSubTab === 'design' && (
                    <div className="bg-white rounded-lg shadow p-6">
                      <h3 className="text-lg font-semibold mb-4">Design Approval</h3>
                      <p className="text-gray-600">Store layout, branding, and interior design approval workflow</p>
                    </div>
                  )}

                  {activeSubTab === 'stock' && (
                    <div className="bg-white rounded-lg shadow p-6">
                      <h3 className="text-lg font-semibold mb-4">Stock Allocation</h3>
                      <p className="text-gray-600">Initial inventory allocation for store launch</p>
                    </div>
                  )}

                  {activeSubTab === 'training' && (
                    <div className="bg-white rounded-lg shadow p-6">
                      <h3 className="text-lg font-semibold mb-4">Training Programs</h3>
                      <p className="text-gray-600">Staff training and onboarding management</p>
                    </div>
                  )}

                  {activeSubTab === 'checklist' && (
                    <div className="bg-white rounded-lg shadow p-6">
                      <h3 className="text-lg font-semibold mb-4">Launch Checklist</h3>
                      <p className="text-gray-600">Pre-launch verification and readiness checklist</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {!loading && activeMainTab === 'active' && (
            <>
              {activeSubTab === 'franchisees' && <ActiveFranchiseesTab franchisees={franchisees} />}
              {activeSubTab === 'plans' && <FranchisePlansTab plans={plans} franchisees={franchisees} />}
              {activeSubTab === 'performance' && <PerformanceDashboardTab franchisees={franchisees} />}
              {activeSubTab === 'daily-ops' && <DailyOperationsTab data={dailyData} loading={loading} />}
              {activeSubTab === 'monthly' && <MonthlyPerformanceTab data={monthlyData} loading={loading} />}
              {activeSubTab === 'commissions' && <CommissionsTab franchisees={franchisees} />}
              {activeSubTab === 'inventory' && <InventoryStockTab franchisees={franchisees} />}
              {activeSubTab === 'support' && <SupportIssuesTab franchisees={franchisees} />}
            </>
          )}
        </div>

        {/* Lead Detail Modal */}
        {showDetailModal && selectedLead && (
          <LeadDetailModal 
            lead={selectedLead}
            onClose={() => {
              setShowDetailModal(false);
              setSelectedLead(null);
            }}
            onRefresh={fetchLeads}
          />
        )}

        {/* Create Lead Modal */}
        <CreateLeadModal
          isOpen={showCreateLeadModal}
          onClose={() => setShowCreateLeadModal(false)}
          onSuccess={() => {
            fetchLeads();
            setShowCreateLeadModal(false);
          }}
        />

        {/* Edit Lead Modal */}
        <EditLeadModal
          isOpen={showEditLeadModal}
          onClose={() => {
            setShowEditLeadModal(false);
            setLeadToEdit(null);
          }}
          lead={leadToEdit}
          onSuccess={() => {
            fetchLeads();
            setShowEditLeadModal(false);
            setLeadToEdit(null);
          }}
        />
      </div>
    </Layout>
  );
}

// ==================== STATUS CARD COMPONENT ====================
function StatusCard({ title, status, icon, amount, subtitle }) {
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-2xl">{icon}</span>
        <span className={`text-2xl ${status ? 'text-green-500' : 'text-gray-300'}`}>
          {status ? '✓' : '○'}
        </span>
      </div>
      <div className="text-sm font-medium text-gray-700">{title}</div>
      {amount > 0 && <div className="text-xs text-gray-500">₹{amount.toLocaleString()}</div>}
      {subtitle && <div className="text-xs text-gray-500 mt-1">{subtitle}</div>}
    </div>
  );
}

// ==================== LEADS: ALL LEADS TAB ====================
function AllLeadsTab({ leads, searchQuery, setSearchQuery, statusFilter, setStatusFilter, openLeadDetail, onEditLead }) {
  const statuses = ['all', 'New', 'Contacted', 'Interested', 'Site Visit', 'Documents Pending', 'Approved', 'Rejected'];
  
  const getStatusColor = (status) => {
    const colors = {
      'New': 'bg-[#2b338030] text-primary',
      'Contacted': 'bg-[#2b338030] text-primary',
      'Interested': 'bg-green-100 text-green-800',
      'Site Visit': 'bg-yellow-100 text-yellow-800',
      'Documents Pending': 'bg-orange-100 text-orange-800',
      'Approved': 'bg-green-100 text-green-800',
      'Rejected': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getTempColor = (temp) => {
    const colors = {
      'Hot': 'text-red-500',
      'Warm': 'text-orange-500',
      'Cold': 'text-primary'
    };
    return colors[temp] || 'text-gray-600';
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = 
      lead.applicant_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.phone?.includes(searchQuery) ||
      lead.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.city?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by name, phone, email, or city..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#2b3380] focus:border-primary"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#2b3380]"
          >
            {statuses.map(status => (
              <option key={status} value={status}>
                {status === 'all' ? 'All Statuses' : status}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lead</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Temperature</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Investment</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredLeads.map((lead) => (
              <tr key={lead.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => openLeadDetail(lead)}>
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900">{lead.applicant_name}</div>
                  <div className="text-xs text-gray-500">{lead.lead_number}</div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900">{lead.phone}</div>
                  <div className="text-xs text-gray-500">{lead.email}</div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900">{lead.city}</div>
                  <div className="text-xs text-gray-500">{lead.state}</div>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(lead.status)}`}>
                    {lead.status}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className={`text-sm font-semibold ${getTempColor(lead.temperature)}`}>
                    {lead.temperature}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  ₹{(lead.investment_capacity / 100000).toFixed(1)}L
                </td>
                <td className="px-6 py-4 text-center">
                  <div className="flex items-center justify-center gap-3">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditLead(lead);
                      }}
                      className="text-primary hover:opacity-70 text-sm font-medium"
                      style={{ color: '#2b3380' }}
                    >
                      ✏️ Edit
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        openLeadDetail(lead);
                      }}
                      className="text-gray-600 hover:text-gray-800 text-sm font-medium"
                    >
                      👁️ View
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {filteredLeads.length === 0 && (
          <div className="text-center py-12 text-gray-500">No leads found</div>
        )}
      </div>
    </div>
  );
}

// ==================== LEADS: MY TASKS TAB ====================
function MyTasksTab({ followups }) {
  const getTypeColor = (type) => {
    const colors = {
      'Call': 'bg-[#2b338030] text-primary',
      'Email': 'bg-[#2b338030] text-primary',
      'Meeting': 'bg-green-100 text-green-800',
      'Site Visit': 'bg-orange-100 text-orange-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 mb-1">Total Tasks</div>
          <div className="text-3xl font-bold text-gray-900">{followups.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 mb-1">Calls</div>
          <div className="text-3xl font-bold text-primary">
            {followups.filter(f => f.followup_type === 'Call').length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 mb-1">Meetings</div>
          <div className="text-3xl font-bold text-green-500">
            {followups.filter(f => f.followup_type === 'Meeting').length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 mb-1">Site Visits</div>
          <div className="text-3xl font-bold text-orange-500">
            {followups.filter(f => f.followup_type === 'Site Visit').length}
          </div>
        </div>
      </div>

      {/* Tasks List */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold">Follow-up Tasks</h3>
        </div>
        
        {followups.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No follow-up tasks found</div>
        ) : (
          <div className="divide-y divide-gray-200">
            {followups.map((task, index) => (
              <div key={index} className="p-6 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${getTypeColor(task.followup_type)}`}>
                        {task.followup_type}
                      </span>
                      <span className="text-sm font-semibold text-gray-900">{task.lead_name}</span>
                      <span className="text-sm text-gray-500">{task.lead_phone}</span>
                    </div>
                    <p className="text-sm text-gray-700 mb-2">{task.notes}</p>
                    {task.next_action && (
                      <div className="text-xs text-gray-500">Next: {task.next_action}</div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-900">
                      {new Date(task.followup_date).toLocaleDateString()}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{task.lead_city}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== LEADS: DASHBOARD TAB ====================
function DashboardTab({ leads, setShowCreateLeadModal }) {
  // Filter states
  const [dateFrom, setDateFrom] = React.useState('');
  const [dateTo, setDateTo] = React.useState('');
  const [selectedEmployee, setSelectedEmployee] = React.useState('all');

  // Status color mapping
  const getStatusColor = (status) => {
    const colors = {
      'New': '#8B5CF6',           // Purple
      'Assigned': '#F59E0B',       // Amber
      'Contacted': '#3B82F6',      // Blue
      'Followup': '#EAB308',       // Yellow
      'Interested': '#10B981',     // Green
      'Site Visit': '#06B6D4',     // Cyan
      'Waiting Token Amount': '#F97316', // Orange
      'Not Interested': '#EF4444', // Red
      'Can Convert Later': '#6B7280', // Gray
      'Token Paid': '#10B981'      // Green
    };
    return colors[status] || '#6B7280';
  };

  // Get unique employees from leads
  const employees = [...new Set(leads.map(l => l.assigned_to).filter(Boolean))];

  // Apply filters
  const filteredLeads = leads.filter(lead => {
    // Date filter
    if (dateFrom && lead.created_at) {
      const leadDate = new Date(lead.created_at).toISOString().split('T')[0];
      if (leadDate < dateFrom) return false;
    }
    if (dateTo && lead.created_at) {
      const leadDate = new Date(lead.created_at).toISOString().split('T')[0];
      if (leadDate > dateTo) return false;
    }

    // Employee filter
    if (selectedEmployee !== 'all' && lead.assigned_to !== selectedEmployee) {
      return false;
    }

    return true;
  });

  // Calculate status distribution on filtered data
  const statusCounts = filteredLeads.reduce((acc, lead) => {
    acc[lead.status] = (acc[lead.status] || 0) + 1;
    return acc;
  }, {});

  // Calculate plan-wise interested leads (on filtered data)
  const interestedByPlan = filteredLeads
    .filter(l => l.status === 'Interested')
    .reduce((acc, lead) => {
      const plan = lead.interested_plan || 'Not Specified';
      acc[plan] = (acc[plan] || 0) + 1;
      return acc;
    }, {});

  // Today's followups due (on filtered data)
  const today = new Date().toISOString().split('T')[0];
  const todaysFollowups = filteredLeads.filter(l => 
    l.status === 'Followup' && l.next_followup_date && l.next_followup_date.split('T')[0] === today
  ).length;

  // This week's site visits (on filtered data)
  const weekStart = new Date();
  weekStart.setDate(weekStart.getDate() - weekStart.getDay());
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 7);
  const thisWeekVisits = filteredLeads.filter(l => 
    l.status === 'Site Visit' && l.site_visit_date &&
    new Date(l.site_visit_date) >= weekStart && new Date(l.site_visit_date) <= weekEnd
  ).length;

  // Conversion metrics (on filtered data)
  const contacted = statusCounts['Contacted'] || 0;
  const tokenPaid = statusCounts['Token Paid'] || 0;
  const conversionRate = contacted > 0 ? ((tokenPaid / contacted) * 100).toFixed(1) : 0;

  // Status categories for cards (using filtered data)
  const statusCategories = [
    { label: 'Total Leads', count: filteredLeads.length, color: '#2b3380', gradient: true },
    { label: 'New Leads', count: statusCounts['New'] || 0, color: '#8B5CF6' },
    { label: 'Assigned Leads', count: statusCounts['Assigned'] || 0, color: '#F59E0B' },
    { label: 'Contacted', count: statusCounts['Contacted'] || 0, color: '#3B82F6' },
    { label: 'Followup', count: statusCounts['Followup'] || 0, color: '#EAB308' },
    { label: 'Interested', count: statusCounts['Interested'] || 0, color: '#10B981' },
    { label: 'Site Visit', count: statusCounts['Site Visit'] || 0, color: '#06B6D4' },
    { label: 'Waiting Token', count: statusCounts['Waiting Token Amount'] || 0, color: '#F97316' },
    { label: 'Not Interested', count: statusCounts['Not Interested'] || 0, color: '#EF4444' },
    { label: 'Can Convert Later', count: statusCounts['Can Convert Later'] || 0, color: '#6B7280' }
  ];

  return (
    <div className="space-y-6">
      {/* Filters Bar */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Date Range Picker */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">From:</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:border-transparent"
                style={{ outlineColor: ZOBOX_PRIMARY }}
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">To:</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:border-transparent"
                style={{ outlineColor: ZOBOX_PRIMARY }}
              />
            </div>
            
            {/* Employee Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Employee:</label>
              <select
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:border-transparent"
                style={{ outlineColor: ZOBOX_PRIMARY }}
              >
                <option value="all">All Employees</option>
                {employees.map(emp => (
                  <option key={emp} value={emp}>{emp}</option>
                ))}
              </select>
            </div>

            {/* Clear Filters */}
            {(dateFrom || dateTo || selectedEmployee !== 'all') && (
              <button
                onClick={() => {
                  setDateFrom('');
                  setDateTo('');
                  setSelectedEmployee('all');
                }}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg"
              >
                ✕ Clear Filters
              </button>
            )}
          </div>

          {/* Results Count */}
          <div className="text-sm text-gray-600">
            Showing <span className="font-bold text-gray-900">{filteredLeads.length}</span> of <span className="font-bold">{leads.length}</span> leads
          </div>
        </div>
      </div>

      {/* Quick Actions Bar */}
      <div className="bg-white rounded-lg shadow p-4 flex items-center justify-between">
        <div className="flex gap-4">
          <button 
            onClick={() => setShowCreateLeadModal(true)}
            className="px-4 py-2 text-white rounded-lg font-medium flex items-center gap-2 hover:opacity-90"
            style={{ backgroundColor: ZOBOX_PRIMARY }}
          >
            <span>+</span> Add New Lead
          </button>
          <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium">
            Import Leads
          </button>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-gray-600">🔥 Today's Followups:</span>
            <span className="font-bold text-red-600">{todaysFollowups}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-600">📅 This Week's Visits:</span>
            <span className="font-bold" style={{ color: '#06B6D4' }}>{thisWeekVisits}</span>
          </div>
        </div>
      </div>

      {/* Main Status Distribution Cards */}
      <div className="grid grid-cols-5 gap-4">
        {statusCategories.map((status, index) => (
          <div 
            key={status.label} 
            className={`rounded-lg shadow p-4 ${status.gradient ? 'text-white' : 'bg-white'}`}
            style={status.gradient ? 
              { background: 'linear-gradient(to right, #2b3380, #1f2660)' } : 
              { borderLeft: `4px solid ${status.color}` }
            }
          >
            <div className={`text-xs mb-2 ${status.gradient ? 'opacity-90' : 'text-gray-600'}`}>
              {status.label}
            </div>
            <div className={`text-3xl font-bold ${status.gradient ? '' : ''}`} 
                 style={!status.gradient ? { color: status.color } : {}}>
              {status.count}
            </div>
            {filteredLeads.length > 0 && (
              <div className={`text-xs mt-1 ${status.gradient ? 'opacity-75' : 'text-gray-500'}`}>
                {((status.count / filteredLeads.length) * 100).toFixed(1)}%
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
          <div className="text-sm text-gray-600 mb-1">Conversion Rate</div>
          <div className="text-3xl font-bold text-green-600">{conversionRate}%</div>
          <div className="text-xs text-gray-500 mt-1">Contacted → Token Paid</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-orange-500">
          <div className="text-sm text-gray-600 mb-1">Hot Leads</div>
          <div className="text-3xl font-bold text-orange-600">
            {(statusCounts['Interested'] || 0) + (statusCounts['Site Visit'] || 0) + (statusCounts['Waiting Token Amount'] || 0)}
          </div>
          <div className="text-xs text-gray-500 mt-1">Needs Immediate Attention</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
          <div className="text-sm text-gray-600 mb-1">In Progress</div>
          <div className="text-3xl font-bold text-blue-600">
            {(statusCounts['Assigned'] || 0) + (statusCounts['Contacted'] || 0) + (statusCounts['Followup'] || 0)}
          </div>
          <div className="text-xs text-gray-500 mt-1">Active Engagement</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-gray-500">
          <div className="text-sm text-gray-600 mb-1">Cold Leads</div>
          <div className="text-3xl font-bold text-gray-600">
            {(statusCounts['Not Interested'] || 0) + (statusCounts['Can Convert Later'] || 0)}
          </div>
          <div className="text-xs text-gray-500 mt-1">Future Potential</div>
        </div>
      </div>

      {/* Interested Leads by Plan */}
      {Object.keys(interestedByPlan).length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Interested Leads by Plan</h3>
          <div className="grid grid-cols-4 gap-4">
            {Object.entries(interestedByPlan).map(([plan, count]) => (
              <div key={plan} className="border rounded-lg p-4 text-center">
                <div className="text-2xl font-bold" style={{ color: '#10B981' }}>{count}</div>
                <div className="text-sm text-gray-600 mt-1">{plan}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Visual Charts */}
      <div className="grid grid-cols-2 gap-6">
        {/* Status Distribution Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Lead Status Distribution</h3>
          <div className="space-y-3">
            {statusCategories.slice(1).map((status) => (
              status.count > 0 && (
                <div key={status.label} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 w-32">{status.label}</span>
                  <div className="flex items-center gap-2 flex-1">
                    <div className="w-full bg-gray-100 rounded-full h-6">
                      <div 
                        className="h-6 rounded-full flex items-center justify-end pr-2 text-white text-xs font-bold"
                        style={{ 
                          width: `${(status.count / filteredLeads.length) * 100}%`,
                          backgroundColor: status.color,
                          minWidth: '30px'
                        }}
                      >
                        {status.count}
                      </div>
                    </div>
                  </div>
                </div>
              )
            ))}
          </div>
        </div>

        {/* Today's Tasks */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Today's Priority Tasks</h3>
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
              <div className="text-2xl">🔥</div>
              <div className="flex-1">
                <div className="font-semibold text-gray-900">Followup Calls Due</div>
                <div className="text-sm text-gray-600">{todaysFollowups} leads need followup today</div>
              </div>
              <div className="text-2xl font-bold text-red-600">{todaysFollowups}</div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-orange-50 rounded-lg">
              <div className="text-2xl">💰</div>
              <div className="flex-1">
                <div className="font-semibold text-gray-900">Waiting for Token</div>
                <div className="text-sm text-gray-600">Ready to close</div>
              </div>
              <div className="text-2xl font-bold text-orange-600">{statusCounts['Waiting Token Amount'] || 0}</div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-cyan-50 rounded-lg">
              <div className="text-2xl">🏢</div>
              <div className="flex-1">
                <div className="font-semibold text-gray-900">Site Visits This Week</div>
                <div className="text-sm text-gray-600">Scheduled visits</div>
              </div>
              <div className="text-2xl font-bold text-cyan-600">{thisWeekVisits}</div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
              <div className="text-2xl">🆕</div>
              <div className="flex-1">
                <div className="font-semibold text-gray-900">New Leads</div>
                <div className="text-sm text-gray-600">Need assignment</div>
              </div>
              <div className="text-2xl font-bold text-purple-600">{statusCounts['New'] || 0}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== JOURNEY: TIMELINE VIEW ====================
function TimelineView({ journey, details }) {
  const stages = [
    { name: 'Lead Created', stage: 'Lead', completed: true, date: journey.created_at },
    { name: 'Token Payment', stage: 'Token Payment', completed: journey.token_paid, date: journey.token_date },
    { name: 'Plan Selected', stage: 'Plan Selection', completed: !!journey.selected_plan },
    { name: 'Agreement Signed', stage: 'Agreement', completed: journey.agreement_signed, date: journey.agreement_date },
    { name: 'Payments Complete', stage: 'Payment Milestones', completed: journey.payment_complete },
    { name: 'GST Approved', stage: 'GST Registration', completed: journey.gst_status === 'Approved' },
    { name: 'NOC Submitted', stage: 'NOC Collection', completed: journey.noc_submitted },
    { name: 'Store Built', stage: 'Store Construction', completed: journey.store_built_confirmed },
    { name: 'Legal Approval', stage: 'Final Approvals', completed: journey.legal_approved, date: journey.legal_approved_date },
    { name: 'Finance Approval', stage: 'Final Approvals', completed: journey.finance_approved, date: journey.finance_approved_date },
    { name: 'Admin Approval', stage: 'Final Approvals', completed: journey.admin_approved, date: journey.admin_approved_date },
    { name: 'Store Created', stage: 'Store Creation', completed: journey.store_created },
    { name: 'Launch', stage: 'Launch', completed: !!journey.actual_launch_date, date: journey.actual_launch_date || journey.expected_launch_date }
  ];

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold mb-6">Journey Timeline</h3>
      <div className="space-y-4">
        {stages.map((stage, index) => (
          <div key={index} className="flex items-start">
            <div className="flex-shrink-0 w-32 text-sm text-gray-600">
              {stage.date ? new Date(stage.date).toLocaleDateString() : stage.completed ? 'Completed' : 'Pending'}
            </div>
            <div className={`flex-grow border-l-2 pl-4 pb-4 ${stage.completed ? 'border-green-500' : 'border-gray-300'}`}>
              <div className="flex items-center">
                <span className={`text-2xl mr-2 ${stage.completed ? 'text-green-500' : 'text-gray-300'}`}>
                  {stage.completed ? '✓' : '○'}
                </span>
                <div>
                  <div className={`font-semibold ${stage.completed ? 'text-gray-900' : 'text-gray-400'}`}>
                    {stage.name}
                  </div>
                  <div className="text-xs text-gray-500">{stage.stage}</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ==================== JOURNEY: GANTT CHART VIEW ====================
function GanttChartView({ journey, details }) {
  const today = new Date();
  const startDate = new Date(journey.created_at);
  const totalDays = journey.expected_launch_date 
    ? Math.ceil((new Date(journey.expected_launch_date) - startDate) / (1000 * 60 * 60 * 24))
    : 90;

  const activities = [
    { name: 'Lead to Token', start: 0, duration: 7, progress: journey.token_paid ? 100 : 50 },
    { name: 'Plan Selection', start: 7, duration: 3, progress: journey.selected_plan ? 100 : 0 },
    { name: 'Agreement', start: 10, duration: 7, progress: journey.agreement_signed ? 100 : 0 },
    { name: 'Payments', start: 17, duration: 30, progress: journey.payment_complete ? 100 : 30 },
    { name: 'GST Registration', start: 20, duration: 15, progress: journey.gst_status === 'Approved' ? 100 : 20 },
    { name: 'Store Setup', start: 35, duration: 20, progress: journey.store_built_confirmed ? 100 : 0 },
    { name: 'NOC & Docs', start: 50, duration: 10, progress: journey.noc_submitted ? 100 : 0 },
    { name: 'Approvals', start: 60, duration: 7, progress: journey.admin_approved ? 100 : 0 },
    { name: 'Store Creation', start: 67, duration: 3, progress: journey.store_created ? 100 : 0 },
    { name: 'Launch Prep', start: 70, duration: 20, progress: 0 }
  ];

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold mb-6">Gantt Chart - Journey to Launch</h3>
      
      {/* Timeline Header */}
      <div className="mb-4 flex items-center text-xs text-gray-600">
        <div className="w-48">Activity</div>
        <div className="flex-1 flex">
          <div className="w-1/4 text-center">Week 1-4</div>
          <div className="w-1/4 text-center">Week 5-8</div>
          <div className="w-1/4 text-center">Week 9-12</div>
          <div className="w-1/4 text-center">Week 13+</div>
        </div>
        <div className="w-20 text-center">Progress</div>
      </div>

      {/* Gantt Bars */}
      <div className="space-y-2">
        {activities.map((activity, index) => {
          const leftPercent = (activity.start / totalDays) * 100;
          const widthPercent = (activity.duration / totalDays) * 100;
          
          return (
            <div key={index} className="flex items-center">
              <div className="w-48 text-sm font-medium text-gray-700 pr-4">{activity.name}</div>
              <div className="flex-1 relative h-8 bg-gray-100 rounded">
                <div
                  className="absolute h-full bg-gray-200 rounded"
                  style={{ left: `${leftPercent}%`, width: `${widthPercent}%` }}
                >
                  <div
                    className="h-full bg-primary rounded transition-all"
                    style={{ width: `${activity.progress}%` }}
                  ></div>
                </div>
              </div>
              <div className="w-20 text-center text-sm font-semibold text-primary">
                {activity.progress}%
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary */}
      <div className="mt-6 p-4 bg-[#2b338015] rounded-lg">
        <div className="flex justify-between text-sm">
          <div>
            <span className="font-semibold">Start Date:</span> {startDate.toLocaleDateString()}
          </div>
          <div>
            <span className="font-semibold">Expected Launch:</span> {journey.expected_launch_date ? new Date(journey.expected_launch_date).toLocaleDateString() : 'TBD'}
          </div>
          <div>
            <span className="font-semibold">Days Elapsed:</span> {Math.ceil((today - startDate) / (1000 * 60 * 60 * 24))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== JOURNEY: PAYMENTS VIEW ====================
function PaymentsView({ journey, details }) {
  const milestones = details.payment_milestones || [];
  const totalPaid = milestones.reduce((sum, m) => sum + (m.paid_amount || 0), 0);
  const totalDue = journey.total_investment;
  const percentPaid = totalDue > 0 ? (totalPaid / totalDue * 100).toFixed(1) : 0;

  return (
    <div className="space-y-6">
      {/* Payment Summary */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Payment Summary</h3>
        <div className="grid grid-cols-3 gap-6">
          <div>
            <div className="text-sm text-gray-600 mb-1">Total Investment</div>
            <div className="text-2xl font-bold">₹{totalDue.toLocaleString()}</div>
          </div>
          <div>
            <div className="text-sm text-gray-600 mb-1">Total Paid</div>
            <div className="text-2xl font-bold text-green-500">₹{totalPaid.toLocaleString()}</div>
          </div>
          <div>
            <div className="text-sm text-gray-600 mb-1">Remaining</div>
            <div className="text-2xl font-bold text-orange-500">₹{(totalDue - totalPaid).toLocaleString()}</div>
          </div>
        </div>
        <div className="mt-4">
          <div className="flex items-center justify-between text-sm mb-1">
            <span>Payment Progress</span>
            <span className="font-semibold">{percentPaid}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div className="bg-primary h-3 rounded-full transition-all" style={{ width: `${percentPaid}%` }}></div>
          </div>
        </div>
      </div>

      {/* Payment Milestones */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold">Payment Milestones</h3>
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Milestone</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Paid</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {milestones.map((milestone) => (
              <tr key={milestone.milestone_id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium">{milestone.milestone_name}</td>
                <td className="px-6 py-4 text-sm text-right">₹{milestone.amount.toLocaleString()}</td>
                <td className="px-6 py-4 text-sm text-right font-semibold text-green-500">
                  ₹{milestone.paid_amount.toLocaleString()}
                </td>
                <td className="px-6 py-4 text-center">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    milestone.status === 'Paid' ? 'bg-green-100 text-green-800' : 
                    milestone.status === 'Overdue' ? 'bg-red-100 text-red-800' : 
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {milestone.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm">
                  {milestone.paid_date ? new Date(milestone.paid_date).toLocaleDateString() : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ==================== JOURNEY: DOCUMENTS VIEW ====================
function DocumentsView({ journey, details }) {
  return (
    <div className="space-y-6">
      {/* GST Document */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">GST Registration</h3>
        {details.gst_registration ? (
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Status:</span>
              <span className={`font-semibold ${details.gst_registration.status === 'Approved' ? 'text-green-500' : 'text-yellow-600'}`}>
                {details.gst_registration.status}
              </span>
            </div>
            {details.gst_registration.gst_number && (
              <div className="flex justify-between">
                <span className="text-gray-600">GST Number:</span>
                <span className="font-semibold">{details.gst_registration.gst_number}</span>
              </div>
            )}
          </div>
        ) : (
          <p className="text-gray-500">No GST registration yet</p>
        )}
      </div>

      {/* NOC Documents */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">NOC Documents</h3>
        {details.noc_document ? (
          <div className="space-y-3">
            <div>
              <span className="text-sm font-medium text-gray-700">Contractor:</span>
              <p className="text-sm">{details.noc_document.contractor_name}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">Items Installed:</span>
              <ul className="list-disc list-inside text-sm">
                {details.noc_document.items_installed?.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">Documents:</span>
              <p className="text-sm text-primary">{details.noc_document.document_urls?.length || 0} files uploaded</p>
            </div>
          </div>
        ) : (
          <p className="text-gray-500">No NOC submitted yet</p>
        )}
      </div>

      {/* Store Built Confirmation */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Store Built Confirmation</h3>
        {details.store_built_confirmation ? (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <span className="text-sm font-medium text-gray-700">Before Photos:</span>
                <p className="text-sm">{details.store_built_confirmation.before_images?.length || 0} images</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">During Construction:</span>
                <p className="text-sm">{details.store_built_confirmation.during_images?.length || 0} images</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">After Completion:</span>
                <p className="text-sm">{details.store_built_confirmation.after_images?.length || 0} images</p>
              </div>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">Completion Date:</span>
              <p className="text-sm">{new Date(details.store_built_confirmation.completion_date).toLocaleDateString()}</p>
            </div>
          </div>
        ) : (
          <p className="text-gray-500">Store construction not confirmed yet</p>
        )}
      </div>
    </div>
  );
}

// ==================== JOURNEY: APPROVALS VIEW ====================
function ApprovalsView({ journey, details }) {
  const approvalsList = details.approvals || [];

  return (
    <div className="space-y-6">
      {/* Approval Status Cards */}
      <div className="grid grid-cols-3 gap-6">
        <ApprovalCard
          title="Legal Approval"
          approved={journey.legal_approved}
          approvedBy={journey.legal_approved_by}
          approvedDate={journey.legal_approved_date}
          icon="⚖️"
        />
        <ApprovalCard
          title="Finance Approval"
          approved={journey.finance_approved}
          approvedBy={journey.finance_approved_by}
          approvedDate={journey.finance_approved_date}
          icon="💰"
        />
        <ApprovalCard
          title="Admin Approval"
          approved={journey.admin_approved}
          approvedBy={journey.admin_approved_by}
          approvedDate={journey.admin_approved_date}
          icon="👤"
        />
      </div>

      {/* Approval History */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Approval History</h3>
        {approvalsList.length > 0 ? (
          <div className="space-y-3">
            {approvalsList.map((approval, index) => (
              <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <div className="font-medium">{approval.approval_type} - {approval.status}</div>
                  <div className="text-sm text-gray-600">{approval.comments}</div>
                </div>
                <div className="text-right text-sm text-gray-500">
                  <div>{approval.approved_by}</div>
                  <div>{new Date(approval.approval_date).toLocaleDateString()}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No approvals recorded yet</p>
        )}
      </div>

      {/* Store Creation Status */}
      {journey.store_created && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-center">
            <span className="text-4xl mr-4">🎉</span>
            <div>
              <h3 className="text-lg font-semibold text-green-800">Store Created Successfully!</h3>
              <p className="text-green-700">Store Code: <strong>{journey.store_code}</strong></p>
              <p className="text-sm text-green-500 mt-1">All approvals completed. Store is now live in the system.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ApprovalCard({ title, approved, approvedBy, approvedDate, icon }) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-3">
        <span className="text-3xl">{icon}</span>
        <span className={`text-3xl ${approved ? 'text-green-500' : 'text-gray-300'}`}>
          {approved ? '✓' : '○'}
        </span>
      </div>
      <div className="text-sm font-medium text-gray-700 mb-2">{title}</div>
      {approved ? (
        <div className="text-xs text-gray-500">
          <div>By: {approvedBy || 'N/A'}</div>
          <div>{approvedDate ? new Date(approvedDate).toLocaleDateString() : 'N/A'}</div>
        </div>
      ) : (
        <div className="text-xs text-gray-500">Pending approval</div>
      )}
    </div>
  );
}

// ==================== ACTIVE: ALL FRANCHISEES TAB ====================
function ActiveFranchiseesTab({ franchisees }) {
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = franchisees.filter(f => 
    f.store_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    f.store_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    f.city?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-6">
        <div className="rounded-lg shadow-lg p-6 text-white" style={{ background: `linear-gradient(to right, #2b3380, #1f2660)` }}>
          <div className="text-sm opacity-90 mb-1">Total Active</div>
          <div className="text-4xl font-bold">{franchisees.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 mb-1">DSA</div>
          <div className="text-4xl font-bold" style={{ color: ZOBOX_PRIMARY }}>
            {franchisees.filter(f => f.ownership_model === 'DSA').length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 mb-1">FOFO</div>
          <div className="text-4xl font-bold text-green-500">
            {franchisees.filter(f => f.ownership_model === 'FOFO').length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 mb-1">FOCO</div>
          <div className="text-4xl font-bold text-orange-500">
            {franchisees.filter(f => f.ownership_model === 'FOCO').length}
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow p-4">
        <input
          type="text"
          placeholder="Search by store name, code, or city..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#2b3380] focus:border-primary"
        />
      </div>

      {/* Franchisees Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((franchisee) => (
          <div key={franchisee.id} className="bg-white rounded-lg shadow hover:shadow-lg transition p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{franchisee.store_name}</h3>
                <p className="text-sm text-gray-500">{franchisee.store_code}</p>
              </div>
              <span className={`px-2 py-1 text-xs rounded-full ${
                franchisee.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {franchisee.status}
              </span>
            </div>
            
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Type:</span>
                <span className="font-medium">{franchisee.store_type}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Model:</span>
                <span className="font-medium">{franchisee.ownership_model}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Location:</span>
                <span className="font-medium">{franchisee.city}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Contact:</span>
                <span className="font-medium">{franchisee.franchisee_phone}</span>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <button className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 text-sm font-medium">
                View Details
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ==================== ACTIVE: FRANCHISEE PLANS TAB ====================
function FranchisePlansTab({ plans, franchisees }) {
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Count franchisees by plan
  const getPlanCount = (planType) => {
    return franchisees.filter(f => f.store_type === planType).length;
  };

  // Get franchisees for a specific plan
  const getFranchiseesByPlan = (planType) => {
    return franchisees.filter(f => f.store_type === planType);
  };

  const formatCurrency = (amount) => {
    if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(1)}L`;
    }
    return `₹${(amount / 1000).toFixed(0)}K`;
  };

  return (
    <div className="space-y-6">
      {/* Plans Overview Cards */}
      <div className="grid grid-cols-4 gap-4">
        {plans.map((plan) => {
          const count = getPlanCount(plan.plan_type);
          return (
            <div key={plan.plan_id} className="bg-white rounded-lg shadow hover:shadow-lg transition p-6 cursor-pointer"
              onClick={() => setSelectedPlan(plan)}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{plan.plan_type}</h3>
                  <p className="text-xs text-gray-500">{plan.ownership_model}</p>
                </div>
                <span className="px-3 py-1 text-xs rounded-full bg-[#2b338030] text-primary font-medium">
                  {count} Active
                </span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Investment:</span>
                  <span className="font-semibold text-gray-900">{formatCurrency(plan.total_investment)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Commission:</span>
                  <span className="font-semibold text-green-500">{plan.commission_percentage}%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Support:</span>
                  <span className="font-semibold text-primary">{formatCurrency(plan.monthly_support)}</span>
                </div>
              </div>
              <button className="w-full mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 text-sm font-medium">
                View Details
              </button>
            </div>
          );
        })}
      </div>

      {/* Selected Plan Details */}
      {selectedPlan && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{selectedPlan.plan_type}</h2>
              <p className="mt-1 text-sm text-gray-600">{selectedPlan.ownership_model} Model</p>
            </div>
            <button 
              onClick={() => setSelectedPlan(null)}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>

          {/* Investment Breakdown - Together */}
          <div className="rounded-lg shadow-lg p-6 text-white mb-6" style={{ background: 'linear-gradient(to right, #2b3380, #1f2660)' }}>
            <h3 className="text-lg font-semibold mb-4">Investment Breakdown</h3>
            <div className="grid grid-cols-4 gap-6">
              <div>
                <div className="text-sm opacity-90 mb-1">Total Investment</div>
                <div className="text-3xl font-bold">{formatCurrency(selectedPlan.total_investment)}</div>
              </div>
              <div>
                <div className="text-sm opacity-90 mb-1">Inventory</div>
                <div className="text-2xl font-bold">{formatCurrency(selectedPlan.inventory_amount)}</div>
              </div>
              <div>
                <div className="text-sm opacity-90 mb-1">Branding & Infra</div>
                <div className="text-2xl font-bold">{formatCurrency(selectedPlan.branding_infra_amount)}</div>
              </div>
              <div>
                <div className="text-sm opacity-90 mb-1">Franchisee Fee</div>
                <div className="text-2xl font-bold">{formatCurrency(selectedPlan.franchisee_fee)}</div>
              </div>
            </div>
          </div>

          {/* Plan Details */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Commission</label>
                <p className="mt-1 text-lg font-semibold text-green-500">{selectedPlan.commission_percentage}% on sales</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Monthly Support</label>
                <p className="mt-1 text-lg font-semibold text-primary">{formatCurrency(selectedPlan.monthly_support)}</p>
                {selectedPlan.support_breakdown && (
                  <div className="mt-2 text-xs text-gray-600 space-y-1">
                    {Object.entries(selectedPlan.support_breakdown).map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <span>{key.replace(/_/g, ' ')}</span>
                        <span className="font-medium">{typeof value === 'number' ? formatCurrency(value) : value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Billing System</label>
                <p className="mt-1 text-sm text-gray-900">{selectedPlan.billing_system}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Area Requirements</label>
                <p className="mt-1 text-sm text-gray-900">{selectedPlan.area_requirement}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Branding Requirements</label>
                <p className="mt-1 text-sm text-gray-900">{selectedPlan.branding_requirements}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Entitled Platforms</label>
                <div className="mt-1 flex gap-2">
                  {selectedPlan.entitled_platforms.map((platform, i) => (
                    <span key={i} className="px-3 py-1 text-xs rounded-full bg-[#2b338030] text-primary font-medium">
                      {platform}
                    </span>
                  ))}
                </div>
              </div>
              {selectedPlan.special_terms && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Special Terms</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedPlan.special_terms}</p>
                </div>
              )}
            </div>
          </div>

          {/* Active Franchisees with this Plan */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Active Franchisees ({getPlanCount(selectedPlan.plan_type)})</h3>
            
            {getFranchiseesByPlan(selectedPlan.plan_type).length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {getFranchiseesByPlan(selectedPlan.plan_type).map((franchisee) => (
                  <div key={franchisee.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow transition">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">{franchisee.store_name}</h4>
                        <p className="text-xs text-gray-500">{franchisee.store_code}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        franchisee.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {franchisee.status}
                      </span>
                    </div>
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Location:</span>
                        <span className="font-medium text-gray-900">{franchisee.city}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Owner:</span>
                        <span className="font-medium text-gray-900">{franchisee.franchisee_name}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Model:</span>
                        <span className="font-medium text-gray-900">{franchisee.ownership_model}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-12 text-center">
                <div className="text-6xl mb-4">🏪</div>
                <h3 className="text-xl font-semibold mb-2 text-gray-900">No Active Franchisees</h3>
                <p className="text-gray-600">No franchisees are currently using this plan</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Plan Comparison Table */}
      {!selectedPlan && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Plan Comparison</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Model</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Investment</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Fee</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Commission</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Support</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Active</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {plans.map((plan) => (
                  <tr key={plan.plan_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{plan.plan_type}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 text-xs rounded-full bg-[#2b338030] text-primary font-medium">
                        {plan.ownership_model}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-semibold text-gray-900">
                      {formatCurrency(plan.total_investment)}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900">
                      {formatCurrency(plan.franchisee_fee)}
                    </td>
                    <td className="px-6 py-4 text-center text-sm font-semibold text-green-600">
                      {plan.commission_percentage}%
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-semibold text-primary">
                      {formatCurrency(plan.monthly_support)}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-sm font-semibold text-gray-900">
                        {getPlanCount(plan.plan_type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => setSelectedPlan(plan)}
                        className="text-primary hover:text-primary/90 text-sm font-medium"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== ACTIVE: PERFORMANCE DASHBOARD TAB ====================
function PerformanceDashboardTab({ franchisees }) {
  const performanceData = franchisees.map(f => ({
    ...f,
    monthly_sales: Math.random() * 500000 + 300000,
    daily_avg: Math.random() * 20000 + 10000,
    target_achievement: Math.random() * 40 + 80,
    customer_rating: (Math.random() * 1.5 + 3.5).toFixed(1)
  })).sort((a, b) => b.monthly_sales - a.monthly_sales);

  return (
    <div className="space-y-6">
      {/* Top Performers */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Top 3 Performers (This Month)</h3>
        <div className="grid grid-cols-3 gap-6">
          {performanceData.slice(0, 3).map((store, index) => (
            <div key={store.id} className={`rounded-lg p-6 ${
              index === 0 ? 'bg-gradient-to-r from-yellow-400 to-yellow-500' :
              index === 1 ? 'bg-gradient-to-r from-gray-300 to-gray-400' :
              'bg-gradient-to-r from-orange-400 to-orange-500'
            } text-white`}>
              <div className="text-6xl mb-2">{['🥇', '🥈', '🥉'][index]}</div>
              <div className="text-xl font-bold mb-1">{store.store_name}</div>
              <div className="text-sm opacity-90">₹{(store.monthly_sales / 100000).toFixed(1)}L</div>
            </div>
          ))}
        </div>
      </div>

      {/* Performance Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold">All Franchisees Performance</h3>
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rank</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Store</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Monthly Sales</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Daily Avg</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Target Achievement</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Rating</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {performanceData.map((store, index) => (
              <tr key={store.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-semibold text-gray-900">#{index + 1}</td>
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900">{store.store_name}</div>
                  <div className="text-xs text-gray-500">{store.store_code}</div>
                </td>
                <td className="px-6 py-4 text-right text-sm font-semibold text-gray-900">
                  ₹{(store.monthly_sales / 100000).toFixed(1)}L
                </td>
                <td className="px-6 py-4 text-right text-sm text-gray-900">
                  ₹{store.daily_avg.toLocaleString()}
                </td>
                <td className="px-6 py-4 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          store.target_achievement >= 100 ? 'bg-green-500' :
                          store.target_achievement >= 80 ? 'bg-[#2b338015]0' : 'bg-orange-500'
                        }`}
                        style={{ width: `${Math.min(store.target_achievement, 100)}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium">{store.target_achievement.toFixed(0)}%</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-center">
                  <span className="text-sm font-semibold text-yellow-600">⭐ {store.customer_rating}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ==================== ACTIVE: DAILY OPERATIONS TAB ====================
function DailyOperationsTab({ data, loading }) {
  const today = new Date().toLocaleDateString();

  if (loading) {
    return <div className="text-center py-12">Loading daily operations...</div>;
  }

  const totalSales = data.reduce((sum, d) => sum + (d.sales?.net_sales || 0), 0);

  return (
    <div className="space-y-6">
      {/* Today's Summary */}
      <div className="grid grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 mb-1">Total Sales Today</div>
          <div className="text-3xl font-bold text-gray-900">₹{(totalSales / 1000).toFixed(0)}K</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 mb-1">Stores Operating</div>
          <div className="text-3xl font-bold text-green-500">{data.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 mb-1">Average per Store</div>
          <div className="text-3xl font-bold text-primary">
            ₹{data.length > 0 ? (totalSales / data.length / 1000).toFixed(0) : 0}K
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 mb-1">Date</div>
          <div className="text-xl font-bold text-gray-900">{today}</div>
        </div>
      </div>

      {/* Store-wise Today's Data */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold">Today's Store Performance</h3>
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Store</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Gross Sales</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Net Sales</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cash</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">UPI</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Reconciled</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((item) => (
              <tr key={item.store.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900">{item.store.store_name}</div>
                  <div className="text-xs text-gray-500">{item.store.store_code}</div>
                </td>
                <td className="px-6 py-4 text-right text-sm text-gray-900">
                  ₹{item.sales?.gross_sales?.toLocaleString() || 0}
                </td>
                <td className="px-6 py-4 text-right text-sm font-semibold text-gray-900">
                  ₹{item.sales?.net_sales?.toLocaleString() || 0}
                </td>
                <td className="px-6 py-4 text-right text-sm text-gray-900">
                  ₹{item.sales?.cash_sales?.toLocaleString() || 0}
                </td>
                <td className="px-6 py-4 text-right text-sm text-gray-900">
                  ₹{item.sales?.upi_sales?.toLocaleString() || 0}
                </td>
                <td className="px-6 py-4 text-center">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    item.sales?.is_reconciled ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {item.sales?.is_reconciled ? 'Done ✓' : 'Pending'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {data.length === 0 && (
          <div className="text-center py-12 text-gray-500">No sales data for today yet</div>
        )}
      </div>
    </div>
  );
}

// ==================== ACTIVE: MONTHLY PERFORMANCE TAB ====================
function MonthlyPerformanceTab({ data, loading }) {
  if (loading) {
    return <div className="text-center py-12">Loading monthly performance...</div>;
  }

  const totalSales = data.reduce((sum, d) => sum + d.total_sales, 0);
  const currentMonth = new Date().toLocaleDateString('default', { month: 'long', year: 'numeric' });

  return (
    <div className="space-y-6">
      {/* Month Summary */}
      <div className="rounded-lg shadow-lg p-6 text-white" style={{ background: 'linear-gradient(to right, #2b3380, #1f2660)' }}>
        <div className="flex justify-between items-center">
          <div>
            <div className="text-sm opacity-90 mb-1">Total Sales - {currentMonth}</div>
            <div className="text-4xl font-bold">₹{(totalSales / 100000).toFixed(1)}L</div>
          </div>
          <div className="text-right">
            <div className="text-sm opacity-90 mb-1">Active Days</div>
            <div className="text-2xl font-bold">{data.length > 0 ? Math.max(...data.map(d => d.days_active)) : 0}</div>
          </div>
        </div>
      </div>

      {/* Store-wise Monthly Performance */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold">Store-wise Monthly Performance</h3>
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Store</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Sales</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Days Active</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Daily Average</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Contribution %</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.sort((a, b) => b.total_sales - a.total_sales).map((item) => {
              const contribution = totalSales > 0 ? (item.total_sales / totalSales * 100) : 0;
              return (
                <tr key={item.store.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{item.store.store_name}</div>
                    <div className="text-xs text-gray-500">{item.store.city}</div>
                  </td>
                  <td className="px-6 py-4 text-right text-sm font-semibold text-gray-900">
                    ₹{(item.total_sales / 100000).toFixed(1)}L
                  </td>
                  <td className="px-6 py-4 text-center text-sm text-gray-900">
                    {item.days_active}
                  </td>
                  <td className="px-6 py-4 text-right text-sm text-gray-900">
                    ₹{item.avg_daily_sales.toLocaleString(undefined, {maximumFractionDigits: 0})}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-20 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-primary h-2 rounded-full"
                          style={{ width: `${contribution}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium">{contribution.toFixed(1)}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ==================== ACTIVE: COMMISSIONS TAB ====================
function CommissionsTab({ franchisees }) {
  const commissionsData = franchisees.map(f => ({
    ...f,
    monthly_sales: Math.random() * 500000 + 300000,
    commission_rate: f.ownership_model === 'DSA' ? 0 : 
                     f.store_type?.includes('Plus') ? 7 :
                     f.store_type?.includes('Pro') ? 9 : 
                     f.store_type?.includes('Maxx') ? 11 : 5
  })).map(f => ({
    ...f,
    commission_amount: f.monthly_sales * (f.commission_rate / 100),
    paid_amount: f.monthly_sales * (f.commission_rate / 100) * 0.7,
    pending_amount: f.monthly_sales * (f.commission_rate / 100) * 0.3
  }));

  const totalCommission = commissionsData.reduce((sum, c) => sum + c.commission_amount, 0);
  const totalPaid = commissionsData.reduce((sum, c) => sum + c.paid_amount, 0);
  const totalPending = commissionsData.reduce((sum, c) => sum + c.pending_amount, 0);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 mb-1">Total Commission</div>
          <div className="text-3xl font-bold text-gray-900">₹{(totalCommission / 100000).toFixed(1)}L</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 mb-1">Paid</div>
          <div className="text-3xl font-bold text-green-500">₹{(totalPaid / 100000).toFixed(1)}L</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 mb-1">Pending</div>
          <div className="text-3xl font-bold text-orange-500">₹{(totalPending / 100000).toFixed(1)}L</div>
        </div>
      </div>

      {/* Commission Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold">Commission Breakdown</h3>
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Store</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Sales</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Rate</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Commission</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Paid</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Pending</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {commissionsData.map((item) => (
              <tr key={item.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900">{item.store_name}</div>
                  <div className="text-xs text-gray-500">{item.store_code}</div>
                </td>
                <td className="px-6 py-4 text-right text-sm text-gray-900">
                  ₹{(item.monthly_sales / 100000).toFixed(1)}L
                </td>
                <td className="px-6 py-4 text-center text-sm font-semibold text-primary">
                  {item.commission_rate}%
                </td>
                <td className="px-6 py-4 text-right text-sm font-semibold text-gray-900">
                  ₹{item.commission_amount.toLocaleString(undefined, {maximumFractionDigits: 0})}
                </td>
                <td className="px-6 py-4 text-right text-sm text-green-500">
                  ₹{item.paid_amount.toLocaleString(undefined, {maximumFractionDigits: 0})}
                </td>
                <td className="px-6 py-4 text-right text-sm text-orange-500">
                  ₹{item.pending_amount.toLocaleString(undefined, {maximumFractionDigits: 0})}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ==================== ACTIVE: INVENTORY & STOCK TAB ====================
function InventoryStockTab({ franchisees }) {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Current Stock Levels</h3>
        <p className="text-gray-600">Inventory tracking feature - Coming soon with real-time stock data</p>
      </div>
    </div>
  );
}

// ==================== ACTIVE: SUPPORT & ISSUES TAB ====================
function SupportIssuesTab({ franchisees }) {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Support Tickets & Issues</h3>
        <p className="text-gray-600">Support ticketing system - Coming soon</p>
      </div>
    </div>
  );
}

// ==================== LEAD DETAIL MODAL ====================
function LeadDetailModal({ lead, onClose, onRefresh }) {
  const [activeDetailTab, setActiveDetailTab] = useState('overview');

  const detailTabs = [
    { id: 'overview', name: 'Overview', icon: '📋' },
    { id: 'followups', name: 'Follow-ups', icon: '✓', count: lead.followups?.length || 0 },
    { id: 'kyc', name: 'KYC Documents', icon: '📄', count: lead.kyc_documents?.length || 0 }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 text-white" style={{ background: 'linear-gradient(to right, #2b3380, #1f2660)' }}>
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold">{lead.lead?.applicant_name}</h2>
              <p className="mt-1 opacity-90">{lead.lead?.lead_number}</p>
            </div>
            <button onClick={onClose} className="text-white hover:text-gray-200">
              <span className="text-2xl">×</span>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-6">
          <nav className="-mb-px flex space-x-8">
            {detailTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveDetailTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeDetailTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.name}
                {tab.count !== undefined && tab.count > 0 && (
                  <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                    activeDetailTab === tab.id ? 'bg-[#2b338030] text-primary' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeDetailTab === 'overview' && <OverviewTab lead={lead.lead} />}
          {activeDetailTab === 'followups' && <FollowupsTab followups={lead.followups || []} />}
          {activeDetailTab === 'kyc' && <KYCTab kyc={lead.kyc_documents || []} />}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
          <button className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90">
            Add Follow-up
          </button>
        </div>
      </div>
    </div>
  );
}

function OverviewTab({ lead }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="text-sm font-medium text-gray-700">Name</label>
          <p className="mt-1 text-sm text-gray-900">{lead.applicant_name}</p>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700">Phone</label>
          <p className="mt-1 text-sm text-gray-900">{lead.phone}</p>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700">Email</label>
          <p className="mt-1 text-sm text-gray-900">{lead.email}</p>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700">City</label>
          <p className="mt-1 text-sm text-gray-900">{lead.city}, {lead.state}</p>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700">Investment Capacity</label>
          <p className="mt-1 text-sm text-gray-900">₹{(lead.investment_capacity / 100000).toFixed(1)}L</p>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700">Source</label>
          <p className="mt-1 text-sm text-gray-900">{lead.source}</p>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700">Status</label>
          <p className="mt-1"><span className="px-2 py-1 text-xs rounded-full bg-[#2b338030] text-primary">{lead.status}</span></p>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700">Temperature</label>
          <p className="mt-1 text-sm font-semibold text-red-500">{lead.temperature}</p>
        </div>
      </div>
      
      {lead.location_address && (
        <div>
          <label className="text-sm font-medium text-gray-700">Address</label>
          <p className="mt-1 text-sm text-gray-900">{lead.location_address}</p>
        </div>
      )}
      
      {lead.remarks && (
        <div>
          <label className="text-sm font-medium text-gray-700">Remarks</label>
          <p className="mt-1 text-sm text-gray-900">{lead.remarks}</p>
        </div>
      )}
    </div>
  );
}

function FollowupsTab({ followups }) {
  if (followups.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <div className="text-4xl mb-4">📝</div>
        <p>No follow-ups recorded yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {followups.map((followup, index) => (
        <div key={index} className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-start justify-between mb-2">
            <div>
              <span className="px-2 py-1 text-xs rounded-full bg-[#2b338030] text-primary">
                {followup.followup_type}
              </span>
              <span className="ml-2 text-sm text-gray-500">
                {new Date(followup.followup_date).toLocaleDateString()}
              </span>
            </div>
            <span className="text-xs text-gray-500">By: {followup.created_by}</span>
          </div>
          <p className="text-sm text-gray-900 mb-2">{followup.notes}</p>
          {followup.next_action && (
            <div className="text-xs text-gray-600 bg-gray-50 rounded p-2">
              <strong>Next Action:</strong> {followup.next_action}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function KYCTab({ kyc }) {
  if (kyc.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <div className="text-4xl mb-4">📄</div>
        <p>No KYC documents uploaded yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {kyc.map((doc, index) => (
        <div key={index} className="border border-gray-200 rounded-lg p-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-gray-700">PAN Number</label>
              <p className="mt-1 text-sm text-gray-900 font-mono">{doc.pan_number}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Aadhar Number</label>
              <p className="mt-1 text-sm text-gray-900 font-mono">{doc.aadhar_number}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">PAN Document</label>
              <p className="mt-1 text-sm text-primary hover:text-primary cursor-pointer">
                📄 {doc.pan_document_path}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Aadhar Document</label>
              <p className="mt-1 text-sm text-primary hover:text-primary cursor-pointer">
                📄 {doc.aadhar_document_path}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Photo</label>
              <p className="mt-1 text-sm text-primary hover:text-primary cursor-pointer">
                🖼️ {doc.photo_path}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Verification Status</label>
              <p className="mt-1">
                <span className={`px-2 py-1 text-xs rounded-full ${
                  doc.verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {doc.verified ? 'Verified ✓' : 'Pending'}
                </span>
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default FranchiseeManagementUnified;
